import type { QueryClient } from '@tanstack/react-query';
import type {
    GetDraftInvoiceResponse,
    GetDraftInvoicesResponse,
} from '@/api/handlers/resources/purchases/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import type { ActiveDraft } from '@/api/models';
import { PurchaseQueryKeys } from './purchaseQueryKeys';

/**
 * Cache writers shared by the draft-invoice mutations. The drafts table on
 * /purchases reads `ApiResponse<GetDraftInvoicesResponse>` envelopes from the
 * `PurchaseQueryKeys.draftInvoiceLists()` caches; these helpers update those
 * rows in place so a save/discard is visible the moment the mutation settles,
 * without waiting for the reconciliation refetch.
 */

type DraftListEnvelope = ApiResponse<GetDraftInvoicesResponse> | undefined;

/** Cache snapshot of every drafts-list entry, for optimistic rollback. */
export type DraftListSnapshot = [readonly unknown[], DraftListEnvelope][];

/**
 * Project a saved draft onto a drafts-table row. `supplier` (a display name)
 * and the three money figures are computed server-side and aren't on the saved
 * draft — they stay null/previous until the reconciliation refetch fills them
 * in.
 */
export const toActiveDraftRow = (
    draft: GetDraftInvoiceResponse,
): ActiveDraft => ({
    id: draft.id,
    currency: draft.currency,
    date: draft.date,
    invoiceNumber: draft.invoiceNumber,
    lastEditedAt: draft.lastEditedAt,
    productCount: draft.lines.length > 0 ? draft.lines.length : null,
    supplier: null,
    linesTotal: null,
    linesTotalTetri: null,
    expensesTotal: null,
    expensesTotalTetri: null,
    totalAmount: null,
    totalAmountTetri: null,
});

const mapListEnvelopes = (
    queryClient: QueryClient,
    update: (page: GetDraftInvoicesResponse) => GetDraftInvoicesResponse,
) => {
    queryClient.setQueriesData<DraftListEnvelope>(
        { queryKey: PurchaseQueryKeys.draftInvoiceLists() },
        (envelope) =>
            envelope?.success
                ? { ...envelope, response: update(envelope.response) }
                : envelope,
    );
};

/**
 * Upsert a saved draft into every drafts-list cache (merge into the existing
 * row, preserving the server-computed `supplier`/`totalAmount`; prepend a new
 * row otherwise) and store the full draft in its detail cache so a resume
 * doesn't refetch what the save response already carried.
 */
export const upsertDraftInvoiceCaches = (
    queryClient: QueryClient,
    draft: GetDraftInvoiceResponse,
): void => {
    const row = toActiveDraftRow(draft);
    mapListEnvelopes(queryClient, (page) => {
        const exists = page.items.some((item) => item.id === row.id);
        return exists
            ? {
                  ...page,
                  items: page.items.map((item) =>
                      item.id === row.id
                          ? {
                                ...row,
                                supplier: item.supplier,
                                linesTotal: item.linesTotal,
                                linesTotalTetri: item.linesTotalTetri,
                                expensesTotal: item.expensesTotal,
                                expensesTotalTetri: item.expensesTotalTetri,
                                totalAmount: item.totalAmount,
                                totalAmountTetri: item.totalAmountTetri,
                            }
                          : item,
                  ),
              }
            : {
                  ...page,
                  items: [row, ...page.items],
                  totalCount: page.totalCount + 1,
              };
    });
    queryClient.setQueryData<ApiResponse<GetDraftInvoiceResponse>>(
        PurchaseQueryKeys.draftInvoice(draft.id),
        { success: true, response: draft },
    );
};

/**
 * Optimistically drop a draft's row from every drafts-list cache. Returns the
 * pre-removal snapshots so a failed DELETE can put them back.
 */
export const removeDraftInvoiceRow = (
    queryClient: QueryClient,
    draftId: string,
): DraftListSnapshot => {
    const snapshot = queryClient.getQueriesData<DraftListEnvelope>({
        queryKey: PurchaseQueryKeys.draftInvoiceLists(),
    }) as DraftListSnapshot;
    mapListEnvelopes(queryClient, (page) => {
        if (!page.items.some((item) => item.id === draftId)) return page;
        return {
            ...page,
            items: page.items.filter((item) => item.id !== draftId),
            totalCount: Math.max(0, page.totalCount - 1),
        };
    });
    return snapshot;
};

/** Roll the drafts-list caches back to a {@link removeDraftInvoiceRow} snapshot. */
export const restoreDraftInvoiceLists = (
    queryClient: QueryClient,
    snapshot: DraftListSnapshot,
): void => {
    snapshot.forEach(([queryKey, envelope]) => {
        queryClient.setQueryData(queryKey, envelope);
    });
};

/** Reconcile with the server: refetch the drafts lists and the summary KPI. */
export const invalidateDraftInvoiceCaches = (
    queryClient: QueryClient,
): void => {
    void queryClient.invalidateQueries({
        queryKey: PurchaseQueryKeys.draftInvoiceLists(),
    });
    void queryClient.invalidateQueries({
        queryKey: PurchaseQueryKeys.summary(),
    });
};
